import { forwardRef, type CSSProperties } from "react";
import { BRAND } from "@metrickle/schema/tokens";
import { MARK_DOT, MARK_LINE, MARK_RADIUS, MARK_SIZE } from "@metrickle/schema/mark";
import type { Shot } from "../../lib/share-shot";

/**
 * The image that goes with a release post. Drawn as plain DOM at its real pixel size and rendered to
 * PNG, so it uses inline styles with fixed colours rather than the dashboard's theme tokens: the card
 * looks the same whatever theme the person making it uses.
 */

export const CARD_FORMATS = {
  landscape: { width: 1200, height: 675, label: "Landscape 1200×675 (X, LinkedIn)" },
  portrait: { width: 1080, height: 1350, label: "Portrait 1080×1350 (LinkedIn)" },
} as const;
export type CardFormat = keyof typeof CARD_FORMATS;
export type CardTheme = "light" | "dark";
export type CardFraming = "screen" | "closeup";

export interface ShareCardProps {
  format: CardFormat;
  theme: CardTheme;
  framing: CardFraming;
  headline: string;
  kicker: string;
  screen: Shot | null;
  closeup: Shot | null;
  exampleData: boolean;
}

const FONT = `ui-sans-serif, system-ui, -apple-system, "Segoe UI", "Helvetica Neue", Arial, sans-serif`;

const PALETTE = {
  light: {
    ink: "#0b0b0b",
    muted: "#52514e",
    bg: `radial-gradient(900px 520px at 88% -12%, rgba(42,120,214,.20), transparent 62%), radial-gradient(700px 420px at -8% 112%, rgba(42,120,214,.10), transparent 60%), linear-gradient(180deg, #f7f9fc 0%, #f1f4f8 100%)`,
    pill: "rgba(42,120,214,.10)",
    pillInk: "#2163b0",
    frame: "1px solid rgba(15,23,42,.10)",
    shadow: "0 1px 2px rgba(15,23,42,.06), 0 24px 60px -12px rgba(16,40,80,.28)",
  },
  dark: {
    ink: "#f4f4f2",
    muted: "#a3a29d",
    bg: `radial-gradient(900px 520px at 88% -12%, rgba(42,120,214,.38), transparent 62%), radial-gradient(700px 420px at -8% 112%, rgba(42,120,214,.16), transparent 60%), linear-gradient(180deg, #0f1217 0%, #0a0c10 100%)`,
    pill: "rgba(96,160,240,.16)",
    pillInk: "#9cc4f5",
    frame: "1px solid rgba(255,255,255,.10)",
    shadow: "0 1px 2px rgba(0,0,0,.4), 0 28px 70px -12px rgba(0,0,0,.65)",
  },
};

function Mark({ size }: { size: number }) {
  return (
    <svg viewBox={`0 0 ${MARK_SIZE} ${MARK_SIZE}`} width={size} height={size} aria-hidden>
      <rect width={MARK_SIZE} height={MARK_SIZE} rx={MARK_RADIUS} fill={BRAND} />
      <path d={MARK_LINE} fill="#fff" />
      <circle {...MARK_DOT} fill="#fff" />
    </svg>
  );
}

/** Long titles step down so they still fit in two or three lines. */
function headlineSize(text: string, portrait: boolean) {
  const n = text.length;
  if (portrait) return n > 70 ? 54 : n > 44 ? 62 : 72;
  return n > 70 ? 38 : n > 44 ? 44 : 52;
}

export const ShareCard = forwardRef<HTMLDivElement, ShareCardProps & { style?: CSSProperties }>(function ShareCard(
  { format, theme, framing, headline, kicker, screen, closeup, exampleData, style },
  ref,
) {
  const { width, height } = CARD_FORMATS[format];
  const portrait = format === "portrait";
  const p = PALETTE[theme];
  const pad = portrait ? 72 : 60;
  const shotTop = portrait ? 430 : 236;


  return (
    <div
      ref={ref}
      style={{
        position: "relative",
        width,
        height,
        overflow: "hidden",
        background: p.bg,
        color: p.ink,
        fontFamily: FONT,
        boxSizing: "border-box",
        ...style,
      }}
    >
      <div style={{ position: "absolute", left: pad, right: pad, top: pad - 4, display: "flex", alignItems: "center", justifyContent: "space-between" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 12, fontSize: portrait ? 30 : 26, fontWeight: 650, letterSpacing: "-0.01em" }}>
          <Mark size={portrait ? 42 : 36} />
          Metrickle
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 10, fontSize: portrait ? 22 : 18, fontWeight: 600 }}>
          {exampleData && <div style={{ padding: "7px 14px", borderRadius: 999, border: p.frame, color: p.muted }}>Example data</div>}
          {kicker && <div style={{ padding: "8px 16px", borderRadius: 999, background: p.pill, color: p.pillInk }}>{kicker}</div>}
        </div>
      </div>

      <div
        style={{
          position: "absolute",
          left: pad,
          right: pad,
          top: portrait ? 170 : 116,
          fontSize: headlineSize(headline, portrait),
          fontWeight: 700,
          letterSpacing: "-0.032em",
          lineHeight: 1.08,
          textWrap: "balance",
          maxHeight: shotTop - (portrait ? 170 : 116) - 24,
          overflow: "hidden",
        }}
      >
        {headline}
      </div>

      <div style={{ position: "absolute", left: pad, right: pad, top: shotTop, bottom: framing === "screen" ? -28 : pad }}>
        {framing === "screen" ? (
          screen ? (
            <div style={{ position: "relative", height: "100%", borderRadius: 16, overflow: "hidden", border: p.frame, boxShadow: p.shadow }}>
              <img src={screen.src} alt="" style={{ display: "block", width: "100%", height: "100%", objectFit: "cover", objectPosition: "top left" }} />
            </div>
          ) : (
            <Placeholder theme={theme} text="Take a shot of the screen" />
          )
        ) : closeup ? (
          <Closeup screen={screen} closeup={closeup} theme={theme} area={{ width: width - pad * 2, height: height - shotTop - pad }} />
        ) : (
          <Placeholder theme={theme} text="Pick part of the screen for a close-up" />
        )}
      </div>
    </div>
  );
});

/**
 * The picked part of the screen, lifted out and enlarged over a faded copy of the whole screen: a
 * clue to the feature rather than all of it.
 */
function Closeup({ screen, closeup, theme, area }: { screen: Shot | null; closeup: Shot; theme: CardTheme; area: { width: number; height: number } }) {
  const p = PALETTE[theme];
  // Padded in the page's own colour so nothing touches the frame. As large as fits, but never past
  // 2.2× its size on screen: beyond that the text looks oddly huge.
  const inset = 28;
  const fit = Math.min((area.width - inset * 2) / closeup.width, (area.height - inset * 2) / closeup.height, 2.2);
  return (
    <div style={{ position: "relative", width: "100%", height: "100%" }}>
      {screen && (
        <div
          style={{
            position: "absolute",
            inset: "-8% -4% -8% -4%",
            borderRadius: 18,
            overflow: "hidden",
            opacity: theme === "dark" ? 0.32 : 0.42,
            filter: "blur(1.5px) saturate(.8)",
            maskImage: "radial-gradient(ellipse at center, #000 35%, transparent 75%)",
            WebkitMaskImage: "radial-gradient(ellipse at center, #000 35%, transparent 75%)",
          }}
        >
          <img src={screen.src} alt="" style={{ width: "100%", height: "100%", objectFit: "cover", objectPosition: "top left" }} />
        </div>
      )}
      <div style={{ position: "absolute", inset: 0, display: "flex", alignItems: "center", justifyContent: "center" }}>
        <div
          style={{
            padding: inset,
            background: closeup.background,
            borderRadius: 16,
            overflow: "hidden",
            border: p.frame,
            boxShadow: p.shadow,
          }}
        >
          <img src={closeup.src} alt="" style={{ display: "block", width: Math.round(closeup.width * fit), height: Math.round(closeup.height * fit) }} />
        </div>
      </div>
    </div>
  );
}

function Placeholder({ theme, text }: { theme: CardTheme; text: string }) {
  const p = PALETTE[theme];
  return (
    <div
      style={{
        height: "100%",
        borderRadius: 16,
        border: `2px dashed ${theme === "dark" ? "rgba(255,255,255,.18)" : "rgba(15,23,42,.16)"}`,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        color: p.muted,
        fontSize: 24,
      }}
    >
      {text}
    </div>
  );
}
